import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Stack, FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './vatsApi';
import type { Vat, Account } from './vatsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    vatId: number | null;
}

export function VatView({ vatId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<Vat>();
    const [accounts, setAccounts] = useState<Account[]>([]);

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const accs = await api.getAccounts();
            setAccounts(accs);
            if (vatId === null) {
                detail.setItem({ id: 0, vatCode: '', vatName: '', vatRate: 0, account: null });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getVat(vatId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [vatId]);

    useEffect(() => { load(); }, [load]);

    const isVatCodeValid = !!detail.item?.vatCode;

    const save = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew
                ? await api.createVat(detail.item!)
                : await api.updateVat(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = async () => {
        if (!detail.item) return;
        if (!window.confirm(t('vat.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deleteVat(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Vat>
            title={`${detail.item?.vatCode ?? ''} ${detail.item?.vatName ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isVatCodeValid}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (<>
                <FormRow label={t('vat.vatCode')}>
                    {!detail.isNew && !detail.isEditing ? (
                        <Typography sx={sx13}>{item.vatCode}</Typography>
                    ) : (
                        <TextField size="small" fullWidth
                            inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                            value={item.vatCode}
                            onChange={e => detail.update('vatCode', e.target.value.toUpperCase())}
                            error={!isVatCodeValid} />
                    )}
                </FormRow>

                <FormRow label={t('vat.vatName')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{item.vatName}</Typography>
                    ) : (
                        <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                            value={item.vatName ?? ''}
                            onChange={e => detail.update('vatName', e.target.value)} />
                    )}
                </FormRow>

                <FormRow label={t('vat.vatRate')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {item.vatRate != null ? `${(item.vatRate * 100).toLocaleString()} %` : ''}
                        </Typography>
                    ) : (
                        <TextField size="small" type="number" fullWidth
                            value={item.vatRate ?? 0}
                            onChange={e => detail.update('vatRate', e.target.value === '' ? 0 : Number(e.target.value))} />
                    )}
                </FormRow>

                <FormRow label={t('vat.account')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {item.account ? `${item.account.accountNumber} ${item.account.accountName}` : ''}
                        </Typography>
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select
                                value={item.account?.id ?? ''}
                                onChange={e => {
                                    const a = accounts.find(x => x.id === e.target.value);
                                    detail.update('account', a ?? null);
                                }}
                            >
                                {[...accounts]
                                    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                    .map(a => (
                                        <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </FormRow>
            </>)}
        </DetailViewShell>
    );
}
